/**
 * Samleliste Types
 * 
 * Minimal types for bulk equipment creation with auto control card generation
 */

export interface EquipmentEntry {
  id: string                          // Temporary client-side ID
  name: string
  serial_number: string
  category_id: string
  manufacturer_id: string
  model?: string
  location?: string
  manufacture_year?: number
  
  // Specs for control card
  wll?: string
  dimension?: string  // Auto-filled based on WLL (e.g., "13 mm" for 2t shackle)
  length?: string     // Length in meters for slings (R2 categories)
  hasHook?: boolean   // Whether fiber sling has a hook attached (R2 categories)
  hookId?: string     // Hook serial number/ID (required if hasHook is true)
  capacity?: number   // Kapasitet (kg) for FAL (Fall Protection) categories
  standard?: string
  grade?: string
  quantity?: number
  batch_id_bow?: string
  batch_id_bolt?: string
  
  // Inspection result
  inspectionStatus?: string  // OK, C, MO, RC, NC
  remarks?: string           // Required when status is not OK
  inspectionPhoto?: string   // Base64 data URL for photo of defect (for non-OK statuses)
  ndtDate?: string           // NDT date for R1/R3 categories (dd.mm.yyyy format)
  generateNdtReport?: boolean // Generate MT/MPI report for chain slings (R1 categories)
  slingParts?: number        // Number of parts/legs for slings (1-5) - determines subcategory
  
  // Validation state
  status: 'pending' | 'valid' | 'error' | 'saving' | 'saved'
  errors: string[]
  
  // Result after save
  equipment_id?: string
  asset_number?: string
  
  // If set, this is an existing equipment (generate new control card, don't create new)
  existing_equipment_id?: string
  
  // Edited specs for existing equipment (saved to DB before control card generation)
  edited_specs?: Array<{
    spec_key: string
    value: string
  }>
  
  // Custom category selection ("Tilpasset" option)
  isCustomCategory?: boolean
  customManufacturerName?: string
  
  // Custom spec values for Tilpasset categories (spec_key -> value)
  customSpecValues?: Record<string, string>
  
  // Shackle type selection (for merged Sjakkel category)
  shackleType?: 'offshore' | 'd-sjakkel' | 'h-sjakkel'
}

export interface SamlelisteDefaults {
  category_id: string
  manufacturer_id: string
  location?: string
}

export interface BulkCreationOptions {
  companyId: string
  generateControlCards: boolean
  inspectorId: string
  inspectorName: string
  inspectorSignature?: string
  responsiblePersonName?: string
  responsiblePersonSignature?: string
  orderNo?: string        // Order number from flow
  serviceOrder?: string   // Service Order (TechnipFMC) - if provided, uses so_no instead of order_no
  location?: string       // Location from flow
}

export interface BulkCreationResult {
  success: boolean
  created: Array<{
    entry_id: string
    equipment_id: string
    asset_number: string
  }>
  failed: Array<{
    entry_id: string
    error: string
  }>
  controlCardBlob?: Blob
}

export interface Category {
  id: string
  code: string
  title_no: string | null
  title_en: string
}

export interface Manufacturer {
  id: string
  name: string
}

// Utility to generate temporary IDs
export const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

